// Överföring av restrisker från Dok 16 (projektörsinlämning) till
// risksammanställning byggskede (execution_followup_items) och
// kvarvarande bruksrisker (residual_use_risks).
import { supabase } from "@/integrations/supabase/client";

export interface TransferSource {
  id: string;
  project_id: string;
  discipline: string;
  residual_risks_construction: string | null;
  residual_risks_use: string | null;
}

export const splitRiskLines = (text: string | null): string[] =>
  (text || "")
    .split(/\r?\n|(?:^|\s)[-•*]\s+/)
    .map((l) => l.replace(/^[-•*\d.)\s]+/, "").trim())
    .filter((l) => l.length > 2);

export interface TransferResult {
  construction: number;
  use: number;
}

export async function transferSubmissionRisks(
  submission: TransferSource,
): Promise<TransferResult> {
  const constructionRisks = splitRiskLines(submission.residual_risks_construction);
  const useRisks = splitRiskLines(submission.residual_risks_use);

  const sourceRef = `dok16:${submission.id}`;

  // Undvik dubbletter vid upprepad överföring.
  const { data: existingExec } = await supabase
    .from("execution_followup_items")
    .select("title")
    .eq("project_id", submission.project_id)
    .eq("source_risk_id", submission.id);
  const existingTitles = new Set((existingExec || []).map((r) => r.title));

  const { data: existingUse } = await supabase
    .from("residual_use_risks")
    .select("risk_description")
    .eq("project_id", submission.project_id)
    .eq("discipline", submission.discipline);
  const existingUseTexts = new Set((existingUse || []).map((r) => r.risk_description));

  const execRows = constructionRisks
    .filter((t) => !existingTitles.has(t))
    .map((title, i) => ({
      project_id: submission.project_id,
      title,
      description: `Överförd från projektörsinlämning (${submission.discipline}).`,
      area: submission.discipline,
      source: sourceRef,
      source_risk_id: submission.id,
      status: "open",
      sort_order: i,
    }));

  const useRows = useRisks
    .filter((t) => !existingUseTexts.has(t))
    .map((risk_description) => ({
      project_id: submission.project_id,
      risk_description,
      discipline: submission.discipline,
      handling: "Överförd från projektörsinlämning (Dok 16).",
      status: "open",
    }));

  if (execRows.length) {
    const { error } = await supabase.from("execution_followup_items").insert(execRows);
    if (error) throw error;
  }
  if (useRows.length) {
    const { error } = await supabase.from("residual_use_risks").insert(useRows);
    if (error) throw error;
  }

  return { construction: execRows.length, use: useRows.length };
}
